/**
 * S8b and S8c. One deploy, and the rollback.
 *
 * PRESENTATION: a right drawer over the timeline, not a page that replaces it.
 * A deploy is an item in a list, and the thing a person does after reading one
 * is look at the one above it. Taking the list off the screen to show a record
 * that is four facts and a ledger would be a route change spent on nothing.
 * The list behind is inert while the drawer is open, so the modality is real
 * rather than visual.
 *
 * ONE ADVANCED MECHANISM, NOT TWO. Started by, the build identifier and the
 * build output sit in a disclosure under the fact list, and the build log sits
 * in a disclosure at the foot of the drawer. Both append, both are one click
 * away in Simple, and neither is reachable only by turning the switch on.
 *
 * THE ROLLBACK BUTTON EXISTS HERE AND NOWHERE ELSE. It is never on a timeline
 * row, because a one click rollback from a list is exactly how a person rolls
 * back the wrong thing. Getting to it costs one click, and that click puts the
 * commit message, the date and the duration on screen first.
 *
 * NO TYPE TO CONFIRM, and that is a rule rather than a mercy. Type to confirm
 * is reserved for the irreversible. A rollback is reversible, you can put the
 * newer version back at any time, and demanding a typed name here would train
 * people to type through dialogs, which is precisely what makes the delete
 * dialog on S19 stop working.
 *
 * A FAILED DEPLOY IS NOT SHOWN HERE. It routes to the S9 treatment, which
 * leads with whether the app is still live rather than with a fact list.
 */

import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { AdvancedDisclosure } from '../../components/patterns/AdvancedDisclosure';
import { AsyncNarration, StepLedger } from '../../components/patterns/AsyncNarration';
import { CopyField } from '../../components/patterns/CopyField';
import type { NarrationStep } from '../../components/patterns/AsyncNarration';
import { DestructiveConfirm } from '../../components/patterns/DestructiveConfirm';
import { Drawer } from '../../components/patterns/Drawer';
import { LogFence } from '../../components/patterns/LogFence';
import { StatusPill } from '../../components/patterns/StatusPill';
import {
  dateLabel,
  pillValue,
  tookLabel,
  triggerWord,
  whenLabel,
} from '../../components/patterns/DeployRow';

import { getDeploy, getProject } from '../../lib/api';
import { rollbackDeploy } from '../../lib/real-api';
import { ApiError } from '../../lib/http';
import { count, durationSeconds } from '../../lib/format';
import { useAsync } from '../../lib/hooks/use-async';
import { useProjectSlug } from '../../lib/hooks/use-project-slug';
import { useTicker } from '../../lib/hooks/use-ticker';
import { t } from '../../lib/i18n/t';
import { scriptFor } from '../../lib/mock/scripts';
import { stateOf } from '../../lib/mock/simulate';
import { useDemoStore } from '../../lib/mock/store';
import { captureEvent } from '../../lib/analytics/posthog';
import type { Deploy } from '../../lib/types';

import { DeploysScreen } from './Deploys';
import '../../styles/components/deploy-detail.css';

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

/**
 * The rollback's three steps, all visible from the moment the dialog swaps,
 * per spec 4.9: every step is on screen from the start, and none of them
 * appears out of nowhere. Before the run exists they are all pending, which is
 * the true state of a rollback that has been asked for and not yet begun.
 */
function RollbackNarration({ id }: { id: string }) {
  const line = useDemoStore((store) => store.timelines.find((one) => one.id === id));
  if (line) return <AsyncNarration timeline={line} />;

  const pending: NarrationStep[] = scriptFor('rollback').steps.map((step) => ({
    label: t(step.label),
    status: 'pending',
  }));
  return <StepLedger steps={pending} />;
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="dfact">
      <dt className="dfact__key">{label}</dt>
      <dd className="dfact__value">{children}</dd>
    </div>
  );
}

export default function DeployDetail() {
  const slug = useProjectSlug() ?? '';
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const back = `/p/${slug}/deploys`;

  const { data, isLoading } = useAsync(async () => {
    const project = await getProject(slug);
    const deploy = await getDeploy(id);
    return { project, deploy };
  }, [slug, id]);

  /* ---- the two runs this screen can start, both off the one clock ---- */

  const timelines = useDemoStore((s) => s.timelines);
  const startTimeline = useDemoStore((s) => s.startTimeline);
  const nextBuildFails = useDemoStore((s) => s.prefs.nextBuildFails);
  const now = useTicker();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rolling, setRolling] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [realRollbackError, setRealRollbackError] = useState<string | null>(null);

  const rollbackLine = timelines.find((one) => one.id === `rollback-${id}`);
  const retryLine = timelines.find((one) => one.id === `retry-${id}`);

  /**
   * The rollback closes the dialog by leaving the screen: the timeline
   * reorders behind the drawer and the trap strip is waiting at the top of it,
   * which is the whole point of having run this. It reads the run's own state
   * rather than a timer, so a slow machine cannot navigate early.
   */
  useEffect(() => {
    if (!rolling || !rollbackLine) return;
    if (stateOf(rollbackLine, now).status !== 'succeeded') return;
    navigate(`${back}?state=rolled-back&to=${id}`, { replace: true });
  }, [rolling, rollbackLine, now, navigate, back, id]);

  useEffect(() => {
    const project = data?.project ?? null;
    const deploy = data?.deploy ?? null;
    if (project && deploy) {
      captureEvent('deploy_detail_viewed', {
        project_slug: project.slug,
        deploy_id: deploy.id,
        is_current: deploy.isCurrent,
        status: deploy.status,
      });
    }
  }, [data]);

  if (isLoading) return <div className="page" />;

  const project = data?.project ?? null;
  const deploy: Deploy | null = data?.deploy ?? null;

  if (!project || !deploy) return <Navigate to={back} replace />;

  // The failure treatment leads with whether the app is still live. A fact
  // list would answer the second question first.
  if (deploy.status === 'didnt-work') {
    return <Navigate to={`${back}/${deploy.id}/failure`} replace />;
  }

  const steps: NarrationStep[] = (deploy?.steps ?? []).map((step) => ({
    label: t(step.label),
    status: step.status,
    elapsed: step.ms > 0 ? durationSeconds(step.ms) : undefined,
  }));

  const retryFailed = retrying && retryLine && stateOf(retryLine, now).status === 'failed';

  const footer = deploy?.isCurrent ? (
    // Flat, no action. There is nothing to do to a version that is already the
    // one being served, and a disabled button would imply there was.
    <p className="dd__running">{t('This is the version your app is running.')}</p>
  ) : !deploy?.rollbackable ? (
    // Past the window where putting a version back is one click. Flat and
    // explanatory for the same reason the line above is: a button that would
    // refuse is worse than no button, and the thing a person needs here is the
    // sentence telling them what does still work.
    <p className="dd__running">
      {t(
        'This version is old enough that we no longer keep its build. To go back to it, put that code back on your branch and push.',
      )}
    </p>
  ) : (
    <Button
      variant="solid"
      onClick={() => {
        captureEvent('rollback_modal_opened', {
          project_slug: project?.slug,
          deploy_id: deploy?.id,
        });
        setConfirmOpen(true);
      }}
    >
      {t('Put this version back')}
    </Button>
  );

  return (
    <>
      {/* The list stays on screen and stays out of reach. */}
      <div className="dd__behind" inert>
        <DeploysScreen behind />
      </div>

      <Drawer
        open
        onClose={() => navigate(back)}
        label={deploy.commitMessage}
        backLabel={t('Deploys')}
        footer={footer}
      >
        <header className="dd__head">
          <StatusPill value={pillValue(deploy.status)} />
          <h1 className="dd__title">{deploy.commitMessage}</h1>
        </header>

        <dl className="dd__facts">
          <Fact label={t('Who')}>{deploy.author}</Fact>
          <Fact label={t('Commit')}>
            <CopyField label={t('Commit')} value={deploy.commitSha} size="inline" />
          </Fact>
          <Fact label={t('Branch')}>
            <span className="mono">{deploy.branch}</span>
          </Fact>
          <Fact label={t('When')}>{whenLabel(deploy.createdAt)}</Fact>
          <Fact label={t('Took')}>
            <span className="mono">{tookLabel(deploy.durationMs)}</span>
          </Fact>
        </dl>

        {/* Advanced appends. It never removes, never reorders, never replaces,
            so these three sit under the five that are always there rather than
            mixed in among them, and they sit behind the one disclosure the
            product uses everywhere else rather than behind a raw mode check.
            This file used to run both disciplines sixty lines apart: the build
            log below was one click away in Simple and these three facts could
            not be reached in Simple at all. */}
        {deploy.trigger || deploy.buildId || deploy.bundleBytes ? (
          <AdvancedDisclosure>
            <dl className="dd__facts">
              {deploy.trigger ? (
                <Fact label={t('Started by')}>{triggerWord(deploy.trigger)}</Fact>
              ) : null}
              {deploy.buildId ? (
                <Fact label={t('Build identifier')}>
                  <CopyField label={t('Build identifier')} value={deploy.buildId} size="inline" />
                </Fact>
              ) : null}
              {deploy.bundleBytes ? (
                <Fact label={t('Build output')}>
                  <span className="mono">
                    {deploy.bundleBytes === 1
                      ? t('1 byte')
                      : t('{n} bytes', { n: count(deploy.bundleBytes) })}
                  </span>
                </Fact>
              ) : null}
            </dl>
          </AdvancedDisclosure>
        ) : null}

        <section className="dd__section" aria-label={t('What this deploy did')}>
          <h2 className="dd__section-title">{t('The build')}</h2>

          {retrying && retryLine ? (
            <AsyncNarration timeline={retryLine} />
          ) : (
            <StepLedger steps={steps} />
          )}

          {retryFailed ? (
            <p className="dd__note">
              {t("That deploy didn't work either. Nothing changed on your live site.")}
            </p>
          ) : null}

          <div className="dd__actions">
            <Button
              variant="outline"
              onClick={() => {
                setRetrying(true);
                startTimeline({
                  id: `retry-${id}`,
                  kind: 'redeploy',
                  scriptId: 'redeploy',
                  subjectId: deploy.id,
                  startedAt: Date.now(),
                  outcome: nextBuildFails ? 'fail@build' : 'success',
                });
              }}
            >
              {t('Try this deploy again')}
            </Button>
          </div>
        </section>

        {deploy.rawLog ? (
          <AdvancedDisclosure label={t('Show the build log')}>
            <LogFence label={t('Build log')} text={deploy.rawLog} />
          </AdvancedDisclosure>
        ) : null}
      </Drawer>

      {/* Reversible, so no typed name. The enumeration is dated, and every
          line of it is something that does NOT change, because the only
          question a person actually has here is whether they are about to
          lose the orders that came in since. */}
      <DestructiveConfirm
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setRolling(true);
          if (!USE_MOCK) {
            // This really does re-push the build now. It used to only move a
            // pointer in the ledger and leave the running app untouched. Still
            // a single request rather than a run to poll: the bytes are already
            // in place, so it either succeeds or reports why not.
            rollbackDeploy(project.id, deploy.id)
              .then(() => navigate(`${back}?state=rolled-back&to=${id}`, { replace: true }))
              .catch((err: unknown) => {
                setRolling(false);
                setRealRollbackError(
                  err instanceof ApiError ? err.message : t('The rollback did not go through. Try again.'),
                );
              });
            return;
          }
          startTimeline({
            id: `rollback-${id}`,
            kind: 'rollback',
            scriptId: 'rollback',
            subjectId: deploy.id,
            startedAt: Date.now(),
            outcome: 'success',
          });
        }}
        title={t('Put this version back?')}
        objectName={deploy.commitSha}
        confirmLabel={t('Put this version back')}
        consequences={[
          {
            kind: 'survives',
            text: t('Your app goes back to the code from {date}.', {
              date: dateLabel(deploy.createdAt),
            }),
          },
          {
            kind: 'survives',
            text: t(
              'Your database does not change. Everything your app has saved since {date} stays exactly where it is.',
              { date: dateLabel(deploy.createdAt) },
            ),
          },
          { kind: 'survives', text: t('Your files do not change.') },
          { kind: 'survives', text: t('You can put the newer version back at any time.') },
        ]}
        narration={
          USE_MOCK ? (
            <RollbackNarration id={`rollback-${id}`} />
          ) : (
            <p className="set__cleanup-say">
              {realRollbackError ?? t('Putting this version back.')}
            </p>
          )
        }
      />
    </>
  );
}
